import { keepPreviousData, useQuery, type UseQueryOptions } from '@tanstack/vue-query';
import type { Ref } from 'vue';
import type { DeathApiRow, DeathRow, RecordsListFetchParams } from '@/types';
import { mapDeathRow } from '@/types';
import ApiRequest from '@/plugins/store/actions/api/ApiRequest';

const DEFAULT_OPTIONS = {
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
};

export const queryKeys = {
    getDeathsRoot: ['deaths'],
    deathsWithParams: (params: Ref<RecordsListFetchParams>) => [...queryKeys.getDeathsRoot, params],
};

/**
 * Every mortality registration in the selected period and location scope, unpaged. The page derives
 * its weekly bins, facet option sets and footer totals from this one array, so there is no
 * server-side paging or filtering contract to keep in step.
 *
 * Pig type is not sent: the caller narrows `locationIds` through
 * `getSelectedSiteIdsFilteredByPigType` before handing them over.
 */
export function useDeaths(fetchParams: Ref<RecordsListFetchParams>, options: UseQueryOptions<DeathRow[]> = {}) {
    return useQuery<DeathRow[]>({
        queryKey: queryKeys.deathsWithParams(fetchParams),
        // Request behaviour goes to the constructor; `send`'s third argument is the body. The wire
        // row is reshaped to `DeathRow` here rather than asserted directly, unlike the sibling
        // insertions/transfers hooks - see `mapDeathRow` for why.
        queryFn: () =>
            new ApiRequest({ skipLoadingIndicator: true })
                .send('get', `records/deaths?${fetchParams.value.period}&location_ids=${fetchParams.value.locationIds.join(',')}`)
                .then((rows: DeathApiRow[]) => rows.map(mapDeathRow)),
        placeholderData: keepPreviousData,
        ...DEFAULT_OPTIONS,
        ...options,
    });
}
